import { useState, useEffect, useMemo } from "react";
import { Button, Modal, DatePicker } from "../../../components";
import { Copy } from "lucide-react";
import axios from "axios";
import {
  CREATE_DELIVERY_SCHEDULES_URL,
  UPDATE_DELIVERY_SCHEDULES_URL,
} from "../../../features/UrlHelper";
import "./DeliveryScheduleFormModal.scss";

const DAYS_OF_WEEK = [
  { id: 1, name: "Pondelok" },
  { id: 2, name: "Utorok" },
  { id: 3, name: "Streda" },
  { id: 4, name: "Štvrtok" },
  { id: 5, name: "Piatok" },
  { id: 6, name: "Sobota" },
  { id: 7, name: "Nedeľa" },
];

// Konvertuje Date objekt alebo string na lokálny "YYYY-MM-DD" string bez timezone posunu
const toLocalDateStr = (val) => {
  if (!val) return "";
  let d;
  if (val instanceof Date) {
    // DatePicker vždy vracia lokálny Date objekt – getDate() atď. je bezpečné
    d = val;
  } else if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    // Čistý "YYYY-MM-DD" string – new Date() by ho parsoval ako UTC midnight,
    // čo by v GMT+2 posunulo dátum dozadu; parsujeme preto lokálne
    const [y, m, day] = val.split("-").map(Number);
    d = new Date(y, m - 1, day);
  } else {
    // ISO string s timezone info (napr. "2026-08-06T22:00:00.000Z") –
    // new Date() ho správne interpretuje a lokálne getter-y vrátia správny deň
    const tmp = new Date(val);
    d = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function DeliveryScheduleFormModal({ isOpen, onClose, editingSchedule, onSave }) {
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [days, setDays] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Vypočíta, ktoré dni v týždni sú povolené na základe dátumového rozsahu
  const allowedDays = useMemo(() => {
    if (!validTo) return [1, 2, 3, 4, 5, 6, 7];

    // Parsujeme ako lokálny čas (nie UTC), aby getDay() vrátil správny deň
    const parseLocal = (str) => {
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    };

    const from = parseLocal(validFrom);
    const to = parseLocal(validTo);
    const diffDays = Math.round((to - from) / (1000 * 60 * 60 * 24));

    if (diffDays >= 6) return [1, 2, 3, 4, 5, 6, 7];

    const allowed = new Set();
    const current = new Date(from);
    while (current <= to) {
      const jsDay = current.getDay(); // 0=Ned, 1=Pon...6=Sob
      const ourDay = jsDay === 0 ? 7 : jsDay; // 1=Pon...7=Ned
      allowed.add(ourDay);
      current.setDate(current.getDate() + 1);
    }

    return [...allowed];
  }, [validFrom, validTo]);


  // Initialize form with today's date or editing data
  useEffect(() => {
    if (isOpen) {
      if (editingSchedule) {
        // Edit mode – normalizujeme na "YYYY-MM-DD" aby sa predišlo timezone posunu
        setValidFrom(editingSchedule.valid_from ? toLocalDateStr(editingSchedule.valid_from) : "");
        setValidTo(editingSchedule.valid_to ? toLocalDateStr(editingSchedule.valid_to) : "");
        setDays(
          editingSchedule.days.map((day) => ({
            day_of_week: day.day_of_week,
            time_from: day.time_from || "",
            time_to: day.time_to || "",
            is_closed: day.is_closed || false,
          }))
        );
      } else {
        // Create mode
        const today = new Date().toISOString().split("T")[0];
        setValidFrom(today);
        setValidTo("");
        setDays(
          DAYS_OF_WEEK.map((d) => ({
            day_of_week: d.id,
            time_from: "08:00",
            time_to: "18:00",
            is_closed: false,
          }))
        );
      }
    }
  }, [isOpen, editingSchedule]);

  const handleDayChange = (index, field, value) => {
    const newDays = [...days];
    newDays[index][field] = value;
    setDays(newDays);
  };

  const copyTimeToNextDays = (currentDayIndex) => {
    const currentDay = days[currentDayIndex];
    const nextIndex = currentDayIndex + 1;
    if (nextIndex >= days.length) return;

    const newDays = [...days];
    newDays[nextIndex] = {
      ...newDays[nextIndex],
      time_from: currentDay.time_from,
      time_to: currentDay.time_to,
      is_closed: currentDay.is_closed,
    };
    setDays(newDays);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validFrom) {
      alert("Prosím, vyber dátum platnosti od");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        valid_from: validFrom,
        valid_to: validTo || null,
        days: days.filter((d) => allowedDays.includes(d.day_of_week)),
      };

      if (editingSchedule) {
        // Update existing
        await axios.put(UPDATE_DELIVERY_SCHEDULES_URL, payload);
      } else {
        // Create new
        await axios.post(CREATE_DELIVERY_SCHEDULES_URL, payload);
      }

      onSave();
    } catch (error) {
      console.error("Error saving schedule:", error);
      alert("Chyba pri ukladaní rozvrhu");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xlarge">
      <div className="modal__header">
        <h2>{editingSchedule ? "Upraviť rozvrh" : "Nový rozvrh"}</h2>
      </div>

      <form onSubmit={handleSubmit} className="delivery-form">
        <div className="modal__body">
          {/* Date Range */}
          <div className="delivery-form__date-range">
            <div className="delivery-form__input-group">
              <DatePicker
                label="Platnosť od:"
                value={validFrom}
                onChange={(val) => setValidFrom(toLocalDateStr(val))}
              />
            </div>

            <div className="delivery-form__input-group">
              <DatePicker
                label="Platnosť do:"
                value={validTo}
                onChange={(val) => setValidTo(toLocalDateStr(val))}
                placeholder="Bez dátumu konca"
              />
              <span className="delivery-form__hint">Voliteľné - ak nie je vyplnené, rozvrh je bez konca</span>
            </div>
          </div>

          {/* Days Grid */}
          <div className="delivery-form__days-header">
            <h4 className="delivery-form__section-title">Časy pre jednotlivé dni</h4>
          </div>

          <div className="delivery-form__days">
            {days.map((day, index) => {
              const dayName = DAYS_OF_WEEK.find((d) => d.id === day.day_of_week)?.name;
              const canCopyToNext = index < days.length - 1;
              const isDayAllowed = allowedDays.includes(day.day_of_week);

              return (
                <div
                  key={day.day_of_week}
                  className={`delivery-form__day-row${isDayAllowed ? "" : " delivery-form__day-row--blocked"}`}
                >
                  <div className="delivery-form__day-name">{dayName}</div>

                  <div className="delivery-form__day-controls">
                    <div className="delivery-form__day-toggle-row">
                      <div className="delivery-form__toggle-wrapper">
                        <input
                          type="checkbox"
                          checked={day.is_closed}
                          onChange={(e) =>
                            handleDayChange(index, "is_closed", e.target.checked)
                          }
                          className="delivery-form__toggle-input"
                          id={`toggle-${index}`}
                          disabled={!isDayAllowed}
                        />
                        <label
                          htmlFor={`toggle-${index}`}
                          className="delivery-form__toggle-switch"
                          style={!isDayAllowed ? { pointerEvents: "none" } : undefined}
                        >
                          <span className="delivery-form__toggle-knob"></span>
                        </label>
                        <span className="delivery-form__toggle-label">Zatvorené</span>
                      </div>

                      <button
                        type="button"
                        className="delivery-form__copy-btn"
                        onClick={() => copyTimeToNextDays(index)}
                        title="Skopírovať časy na nasledujúci deň"
                        style={
                          !isDayAllowed || index === days.length - 1
                            ? { visibility: "hidden", pointerEvents: "none" }
                            : undefined
                        }
                      >
                        <Copy size={18} />
                      </button>
                    </div>

                    {!day.is_closed && (
                      <div className="delivery-form__time-inputs">
                        <div className="delivery-form__time-input-wrapper">
                          <input
                            type="time"
                            value={day.time_from}
                            onChange={(e) =>
                              handleDayChange(index, "time_from", e.target.value)
                            }
                            className="delivery-form__time-input"
                            placeholder="Od"
                            disabled={!isDayAllowed}
                          />
                        </div>
                        <span className="delivery-form__time-separator">–</span>
                        <div className="delivery-form__time-input-wrapper">
                          <input
                            type="time"
                            value={day.time_to}
                            onChange={(e) =>
                              handleDayChange(index, "time_to", e.target.value)
                            }
                            className="delivery-form__time-input"
                            placeholder="Do"
                            disabled={!isDayAllowed}
                          />
                        </div>
                      </div>
                    )}

                    {day.is_closed && (
                      <div className="delivery-form__closed-placeholder">
                        Uzavretý
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal__footer">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Zrušiť
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Ukladám..." : "Uložiť rozvrh"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default DeliveryScheduleFormModal;
